import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  FileAddOutlined,
  NumberOutlined,
  WarningOutlined,
  TagOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  CommentOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { docGuideSection } from '@/components/document-builder/docGuide';
import { HowToCard } from '@/components/how-to-card';
import {
  listEmploymentContracts,
  createEmploymentContract,
  updateEmploymentContract,
  deleteEmploymentContract,
} from '@/services/employmentContracts.service';
import {
  ContractTypeFields,
  contractSequenceKey,
  contractTypeColor,
  isContractType,
  contractTypeLabel,
} from './contractTypes';

const DATE_FORMAT = 'DD.MM.YYYY';
// Art. 37 of the Labour Law: fixed-term work with one employer, 24 months.
const FIXED_TERM_LIMIT_MONTHS = 24;

const formatDate = (value) => (value ? dayjs(value).format(DATE_FORMAT) : '—');
const toApiDate = (value) => (value ? dayjs(value).format('YYYY-MM-DD') : null);

const ROMAN = [
  [10, 'X'],
  [9, 'IX'],
  [5, 'V'],
  [4, 'IV'],
  [1, 'I'],
];
const toRoman = (value) => {
  if (value >= 40) return String(value);
  let rest = value;
  let out = '';
  for (const [n, sym] of ROMAN) {
    while (rest >= n) {
      out += sym;
      rest -= n;
    }
  }
  return out;
};

// Months of a fixed-term contract, counting the first and the last day.
const contractMonths = (entry) =>
  entry.startDate && entry.endDate
    ? Math.max(
        0,
        dayjs(entry.endDate)
          .add(1, 'day')
          .diff(dayjs(entry.startDate), 'month', true)
      )
    : 0;

const formatMonths = (value) => {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded)
    ? String(rounded)
    : String(rounded).replace('.', ',');
};

function ContractFormModal({
  open,
  entry,
  defaultType,
  onClose,
  onSubmit,
  submitting,
}) {
  const { t } = useTranslation('forms');
  const [form] = Form.useForm();
  const kind = Form.useWatch('kind', form) || 'contract';
  const employmentType = Form.useWatch('employmentType', form) || 'limited';

  const initialValues = useMemo(
    () => ({
      kind: entry?.kind || 'contract',
      employmentType: entry?.employmentType || defaultType || 'limited',
      otherTypeName: entry?.otherTypeName || '',
      number: entry?.number || '',
      signedDate: entry?.signedDate ? dayjs(entry.signedDate) : null,
      startDate: entry?.startDate ? dayjs(entry.startDate) : null,
      endDate: entry?.endDate ? dayjs(entry.endDate) : null,
      reason: entry?.reason || '',
      subject: entry?.subject || '',
      note: entry?.note || '',
    }),
    [entry, defaultType]
  );

  const handleOk = async () => {
    const values = await form.validateFields();
    onSubmit({
      ...values,
      signedDate: toApiDate(values.signedDate),
      startDate: toApiDate(values.startDate),
      endDate: toApiDate(values.endDate),
    });
  };

  const required = [{ required: true }];
  const isLimitedContract = kind === 'contract' && employmentType === 'limited';
  // Every contract but an open-ended one can end; only fixed-term must.
  const hasEndDate = kind === 'contract' && employmentType !== 'unlimited';

  return (
    <Modal
      open={open}
      title={
        entry?._id
          ? t('editContractEntry', 'Izmena ugovora / aneksa')
          : t('addContractEntry', 'Novi ugovor / aneks')
      }
      okText={t('save', 'Sačuvaj')}
      cancelText={t('cancel', 'Otkaži')}
      okButtonProps={{ shape: 'round', loading: submitting }}
      cancelButtonProps={{ shape: 'round' }}
      onOk={handleOk}
      onCancel={onClose}
      destroyOnHidden
      maskClosable={false}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        preserve={false}
      >
        <Form.Item name="kind" label={t('contractEntryKind', 'Vrsta upisa')}>
          <Radio.Group disabled={Boolean(entry?._id)}>
            <Radio value="contract">
              {t('contractEntryKind_contract', 'Ugovor')}
            </Radio>
            <Radio value="annex">{t('contractEntryKind_annex', 'Aneks')}</Radio>
          </Radio.Group>
        </Form.Item>
        {kind === 'contract' && <ContractTypeFields type={employmentType} />}
        <Form.Item
          name="number"
          label={
            kind === 'annex'
              ? t('annexNumber', 'Broj aneksa')
              : t('contractNumber', 'Broj ugovora')
          }
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="signedDate"
          label={t('contractSignedDate', 'Datum zaključenja')}
          rules={kind === 'annex' ? required : []}
        >
          <DatePicker style={{ width: '100%' }} format={DATE_FORMAT} />
        </Form.Item>
        <Form.Item
          name="startDate"
          label={
            kind === 'annex'
              ? t('annexStartDate', 'Važi od')
              : t('contractStartDate', 'Početak rada po ugovoru')
          }
          rules={kind === 'contract' ? required : []}
        >
          <DatePicker style={{ width: '100%' }} format={DATE_FORMAT} />
        </Form.Item>
        {hasEndDate && (
          <Form.Item
            name="endDate"
            label={t('contractEndDate', 'Završetak ugovora')}
            rules={isLimitedContract ? required : []}
          >
            <DatePicker style={{ width: '100%' }} format={DATE_FORMAT} />
          </Form.Item>
        )}
        {isLimitedContract && (
          <Form.Item
            name="reason"
            label={t('contractReason', 'Razlog rada na određeno')}
          >
            <Input />
          </Form.Item>
        )}
        {kind === 'annex' && (
          <Form.Item
            name="subject"
            label={t('annexSubject', 'Šta se aneksom menja')}
          >
            <Input.TextArea rows={2} />
          </Form.Item>
        )}
        <Form.Item name="note" label={t('contractNote', 'Napomena')}>
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

/**
 * The employee's contract history: employment contracts (numbered I, II,
 * III… in order) and annexes, with the fixed-term months used against the
 * 24-month limit. Entries come from documents created from contract/annex
 * templates, or are typed in by hand (e.g. contracts signed before the
 * platform).
 */
export default function EmploymentContractsView({
  employeeId,
  canEdit = false,
  profileEmploymentType,
  // The employee profile keeps the guide closed until asked for.
  guideCollapsed = false,
}) {
  const { t } = useTranslation('forms');
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null);
  const queryKey = ['employmentContracts', employeeId];

  const { data: entries = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => listEmploymentContracts(employeeId),
    enabled: Boolean(employeeId),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey });
  const onError = (error) =>
    message.error(
      error?.response?.data?.message || t('saveFailed', 'Čuvanje nije uspelo.')
    );

  const saveMutation = useMutation({
    mutationFn: (values) =>
      editing?._id
        ? updateEmploymentContract(editing._id, values)
        : createEmploymentContract({ ...values, employee: employeeId }),
    onSuccess: () => {
      setEditing(null);
      refresh();
    },
    onError,
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteEmploymentContract(id),
    onSuccess: refresh,
    onError,
  });

  // Employment contracts are numbered together, every other type on its own.
  const rows = useMemo(() => {
    const counters = {};
    return entries.map((entry) => {
      if (entry.kind === 'annex') return { ...entry, ordinal: '' };
      const key = contractSequenceKey(entry);
      counters[key] = (counters[key] || 0) + 1;
      return { ...entry, ordinal: toRoman(counters[key]) };
    });
  }, [entries]);

  // A new contract starts from the profile's type, else the last contract's.
  const defaultType = useMemo(() => {
    if (isContractType(profileEmploymentType)) return profileEmploymentType;
    const lastContract = [...entries]
      .reverse()
      .find((entry) => entry.kind !== 'annex');
    return lastContract?.employmentType || 'limited';
  }, [entries, profileEmploymentType]);

  const fixedTermMonths = useMemo(
    () =>
      entries
        .filter(
          (entry) =>
            entry.kind !== 'annex' && entry.employmentType === 'limited'
        )
        .reduce((sum, entry) => sum + contractMonths(entry), 0),
    [entries]
  );

  const columns = [
    { title: '#', dataIndex: 'ordinal', width: 48 },
    {
      title: t('contractEntryKind', 'Vrsta upisa'),
      render: (_, entry) =>
        entry.kind === 'annex' ? (
          <Tag color="purple">{t('contractEntryKind_annex', 'Aneks')}</Tag>
        ) : (
          <Tag color={contractTypeColor(entry.employmentType)}>
            {contractTypeLabel(t, entry)}
          </Tag>
        ),
    },
    {
      title: t('contractNumberShort', 'Broj'),
      dataIndex: 'number',
      render: (v) => v || '—',
    },
    {
      title: t('contractSignedDate', 'Datum zaključenja'),
      dataIndex: 'signedDate',
      render: formatDate,
    },
    {
      title: t('contractPeriod', 'Period'),
      render: (_, entry) =>
        entry.kind === 'annex'
          ? `${t('annexStartDate', 'Važi od')} ${formatDate(entry.startDate)}`
          : `${formatDate(entry.startDate)} – ${entry.endDate ? formatDate(entry.endDate) : '…'}`,
    },
    {
      title: t('contractDetailsColumn', 'Razlog / predmet'),
      render: (_, entry) => entry.reason || entry.subject || entry.note || '—',
    },
    {
      title: t('contractDocument', 'Dokument'),
      render: (_, entry) =>
        entry.document ? (
          <Typography.Text>
            {entry.document.title || t('contractDocument', 'Dokument')}
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary">
            {t('contractEnteredByHand', 'Unet ručno')}
          </Typography.Text>
        ),
    },
    ...(canEdit
      ? [
          {
            width: 88,
            render: (_, entry) => (
              <Space size={4}>
                <Button
                  size="small"
                  shape="round"
                  icon={<EditOutlined />}
                  onClick={() => setEditing(entry)}
                />
                <Popconfirm
                  title={t(
                    'deleteContractEntryConfirm',
                    'Obrisati ovaj upis iz istorije ugovora?'
                  )}
                  okText={t('yes', 'Da')}
                  cancelText={t('no', 'Ne')}
                  okButtonProps={{ shape: 'round' }}
                  cancelButtonProps={{ shape: 'round' }}
                  onConfirm={() => deleteMutation.mutate(entry._id)}
                >
                  <Button
                    size="small"
                    shape="round"
                    danger
                    icon={<DeleteOutlined />}
                  />
                </Popconfirm>
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-3">
      <HowToCard
        title={t('docGuide.title')}
        sections={[
          docGuideSection(
            t,
            'contracts',
            [FileAddOutlined, EditOutlined, NumberOutlined, WarningOutlined],
            [null, canEdit ? () => setEditing({}) : null]
          ),
          docGuideSection(t, 'contractFields', [
            TagOutlined,
            ClockCircleOutlined,
            CalendarOutlined,
            CommentOutlined,
          ]),
        ]}
        // Opens by itself while the employee has no contracts recorded yet.
        open={
          guideCollapsed ? false : isLoading ? undefined : entries.length === 0
        }
      />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Typography.Text type="secondary">
          {t('fixedTermSummary', {
            defaultValue:
              'Na određeno ukupno: {{total}} mes. — do granice od 24 meseca ostalo: {{left}} mes.',
            total: formatMonths(fixedTermMonths),
            left: formatMonths(
              Math.max(0, FIXED_TERM_LIMIT_MONTHS - fixedTermMonths)
            ),
          })}
        </Typography.Text>
        {canEdit && (
          <Button
            icon={<PlusOutlined />}
            type="primary"
            shape="round"
            onClick={() => setEditing({})}
          >
            {t('addContractEntry', 'Novi ugovor / aneks')}
          </Button>
        )}
      </div>

      {fixedTermMonths > FIXED_TERM_LIMIT_MONTHS && (
        <Alert
          type="warning"
          showIcon
          message={t(
            'fixedTermOverLimit',
            'Rad na određeno prelazi 24 meseca. Proverite da li je neki ugovor trebalo da bude na neodređeno.'
          )}
        />
      )}

      <Table
        rowKey="_id"
        size="small"
        loading={isLoading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: true }}
        locale={{
          emptyText: t('noContractEntries', 'Još nema upisanih ugovora.'),
        }}
      />

      <ContractFormModal
        open={Boolean(editing)}
        entry={editing}
        defaultType={defaultType}
        submitting={saveMutation.isPending}
        onClose={() => setEditing(null)}
        onSubmit={(values) => saveMutation.mutate(values)}
      />
    </div>
  );
}
